import React, { useState, useEffect, useRef } from "react";
import { Chart } from "chart.js";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import ApiService from "../../_services/ApiService";
import { useHistory, useParams } from "react-router-dom";
import "./TestAnalysis.styles.css";

const useStylesBarGraph = makeStyles({
  card: {
    width: "100%",
    height: "fit-content",
    margin: "10px",
  },
});

Chart.defaults.global.defaultFontStyle = "900";

const useStylesRadarGraph = makeStyles({
  card: {
    width: "80%",
    height: "fit-content",
    margin: "5px",
  },
});

const subjectColors = [
  "darkblue",
  "yellow",
  "lightblue",
  "red",
  "blue",
  "green",
  "brown",
];

const testColors = [
  "red",
  "blue",
  "green",
  "yellow",
  "brown",
  "darkblue",
  "lightblue",
];

const StackedBarLineGraph = ({
  testLabels,
  subjectLabels,
  testData,
  totalMarksData,
}) => {
  const [chartRef, setChartRef] = useState(null);
  useEffect(() => {
    if (chartRef) {
      new Chart(chartRef, {
        type: "bar",
        data: {
          labels: testLabels,
          datasets: [
            ...subjectLabels.map((subject, index) => ({
              label: subject,
              data: testData.map((test) => test[index]),
              backgroundColor: subjectColors[index],
              borderColor: subjectColors[index],
              borderWidth: 1,
            })),
            {
              type: "line",
              label: "Total Marks",
              data: totalMarksData,
              fill: false,
              borderWidth: 1,
              borderColor: "rgb(75, 192, 192)",
              responsive: true,
            },
          ],
        },
        options: {
          scales: {
            xAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: "Group By: TestName",
                },
                ticks: {
                  autoSkip: false,
                  source: "labels",
                  labels: testLabels,
                },
              },
            ],
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
          responsive: true,
          maintainAspectRatio: false,
          hover: {
            animationDuration: 0,
          },
          animation: {
            duration: 1,
            onComplete: function () {
              var chartInstance = this.chart,
                ctx = chartInstance.ctx;
              ctx.font = Chart.helpers.fontString(
                Chart.defaults.global.defaultFontSize,
                Chart.defaults.global.defaultFontStyle,
                Chart.defaults.global.defaultFontFamily
              );
              ctx.fillStyle = this.chart.config.options.defaultFontColor;
              ctx.textAlign = "center";
              ctx.textBaseline = "bottom";

              this.data.datasets.forEach(function (dataset, i) {
                var meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach(function (bar, index) {
                  var data = dataset.data[index];
                  ctx.fillText(data, bar._model.x, bar._model.y - 5);
                });
              });
            },
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              var value =
                data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " marks";
            },
          },
        },
      });
    }
  }, [testLabels, subjectLabels, testData, totalMarksData, chartRef]);

  return (
    <div style={{ height: "360px", width: "100%" }}>
      <canvas ref={(ref) => setChartRef(ref)} />
    </div>
  );
};

const RadarGraph = ({ testLabels, subjectLabels, testData }) => {
  const chartRef = useRef(null);
  useEffect(() => {
    if (chartRef.current) {
      new Chart(chartRef.current, {
        type: "radar",
        data: {
          labels: testLabels,
          datasets: subjectLabels.map((subject, index) => ({
            label: subject,
            data: testData.map((test) => test[index]),
            backgroundColor: `rgba(0,0,255,0)`,
            borderColor: subjectColors[index],
            pointBackgroundColor: subjectColors[index],
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: subjectColors[index],
          })),
        },
        options: {},
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              var value =
                data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " marks";
            },
          },
        },
        responsive: true,
      });
    }
  }, [testLabels, subjectLabels, testData]);

  return (
    <div style={{ width: "100%" }}>
      <canvas ref={chartRef} />
    </div>
  );
};

const SpiderGraph = ({ subjectLabels, testLabels, testData }) => {
  const chartRef = useRef(null);
  useEffect(() => {
    if (chartRef.current) {
      new Chart(chartRef.current, {
        type: "radar",
        data: {
          labels: subjectLabels,
          datasets: testLabels.map((test, index) => ({
            label: test,
            data: testData[index],
            backgroundColor: `rgba(0,0,255,0)`,
            borderColor: testColors[index],
            pointBackgroundColor: testColors[index],
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: testColors[index],
          })),
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              var value =
                data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " marks";
            },
          },
        },
        responsive: true,
      });
    }
  }, [subjectLabels, testLabels, testData]);

  return (
    <div style={{ width: "100%" }}>
      <canvas ref={chartRef} />
    </div>
  );
};

const TabularData = ({ testPerformances, subjectLabels }) => {
  const { tests } = testPerformances;
  const { id } = useParams();
  const history = useHistory();
  return (
    <div className="rank-wrapper analysis-tab-data-wrapper">
      <div className="rank-div rank-heading analysis-tab-heading">
        <div className="rank-details larger-grid">
          <h5>Test Name</h5>
        </div>
        <div className="rank-details">
          <h5>Max Marks</h5>
        </div>
        {subjectLabels?.map((subjectLabel, i) => (
          <div key={i} className="rank-details">
            <h5>{subjectLabel}</h5>
          </div>
        ))}
        <div className="rank-details">
          <h5>My Score</h5>
        </div>
        <div className="rank-details">
          <h5>Result</h5>
        </div>
      </div>
      {tests?.map((test, i) => (
        <div key={i} className="rank-div">
          <div className="rank-details larger-grid">
            <h6>{test.test_name}</h6>
          </div>
          <div className="rank-details">
            <h6>{test.maxMarks}</h6>
          </div>
          {subjectLabels?.map((subjectLabel, j) => (
            <div key={j} className="rank-details">
              <h6>{test.subjects[subjectLabel]?.toString() || "--"}</h6>
            </div>
          ))}
          <div className="rank-details">
            <h6>{test.total_marks}</h6>
          </div>
          <div className="rank-details">
            <button
              onClick={() => history.push(`/test-result/${id}/${test.test_id}`)}
              className="download result-button"
            >
              Result
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const AttemptsBar = ({ topic }) => {

  const right = Number(topic?.numberOfCorrects);
  const noOfAttempts = Number(topic?.numberOfAttempts)
  const wrong = (noOfAttempts || 0) - (right || 0);
  const notAttempt = Number(topic.numberOfQuestions);

  return (

    <div className="profiling-full-width">
      <div
        style={{
          gridTemplateColumns: `${right || 0}fr ${wrong || 0}fr ${notAttempt || 0
            }fr`,
        }}
        className="profiling-progress-bar question-neutral"
      >
        <div className="question-success"></div>
        <div className="question-fail"></div>
        <div className="question-neutral"></div>
      </div>
      <div className="profiling-heading profiling-center-text">
        <span className="question-success-text">{right || 0}</span>/
        <span className="question-fail-text">{wrong || 0}</span>/
        <span className="question-neutral-text">{notAttempt || 0}</span>
      </div>
    </div>
  )
};


const AccuracyBar = ({ topic }) => {
  const right = Number(topic?.numberOfCorrects);
  const noOfAttempts = Number(topic?.numberOfAttempts)
  const wrong = (noOfAttempts || 0) - (right || 0);

  return (

    <div className="profiling-full-width">
      <div
        style={{
          gridTemplateColumns: `${right || 0}fr 
                        ${wrong || 0}fr`,
        }}
        className="profiling-progress-bar question-neutral"
      >
        <div className="question-success animate-progress"></div>
        <div className="question-neutral"></div>
      </div>
      <div className="profiling-heading profiling-center-text">
        {(
          ((right || 0) / ((wrong || 0) + (right || 0))) * 100 ||
          0
        )?.toFixed(1)}
        %
      </div>
    </div>
  )
}

const PerformanceProfiling = ({ coursesData }) => {
  return (
    <div className="rank-wrapper analysis-tab-data-wrapper">
      <div className="rank-div rank-heading analysis-tab-heading">
        <div className="rank-details larger-grid">
          <h5>Topic Name</h5>
        </div>
        <div className="rank-details">
          <h5>Exposed  <i className="test-result-info">
            i{" "}
            <span className="test-result-legend test-result-right-legend analysis-legend">
              The total number of questions presented to you from the topic across all the tests you have taken
            </span>
          </i></h5>
        </div>
        <div className="rank-details">
          <h5>Marks Scored
            <i className="test-result-info">
              i{" "}
              <span className="test-result-legend test-result-right-legend analysis-legend">
                Total marks you obtained based on your correct answers.
              </span>
            </i>
          </h5>
        </div>
        <div className="rank-details">
          <h5>Attempted  <i className="test-result-info">
            i{" "}
            <span className="test-result-legend test-result-left-legend analysis-legend">
            The number of questions you attempted to answer out of the total questions exposed. 
            </span>
          </i></h5>
        </div>
        <div className="rank-details">
          <h5>Accuracy<i className="test-result-info">
          i{" "}
          <span className="test-result-legend test-result-left-legend analysis-legend">
          The percentage of correct answers out of the questions attempted. 
          </span>
        </i>
          
          </h5>
        </div>
      </div>
      {coursesData?.map((course) =>
        course.topics?.map((topic, i) => (
          <div key={i} className="rank-div">
            <div className="rank-details larger-grid">
              <h6>{topic.topicName}</h6>
            </div>
            <div className="rank-details">
              <h6>{`${topic.numberOfAttempts}/${topic.numberOfQuestions}`}</h6>
            </div>
            <div className="rank-details">
              <h6>{`${topic.score}/${topic.outOf}  (${topic.percentage}%)`}</h6>
            </div>
            <div className="rank-details">
              <AttemptsBar topic={topic} />
            </div>
            <div className="rank-details">
              <AccuracyBar topic={topic} />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const TestAnalysis = () => {
  const [testPerformances, setTestPerformances] = useState({});
  const [subjectLabels, setSubjectLabels] = useState([]);
  const [testData, setTestData] = useState([]);
  const [testLabels, setTestLabels] = useState([]);
  const [totalMarksData, setTotalMarksData] = useState([]);
  const [displaying, setDisplaying] = useState("tabular");
  const [coursesData, setCoursesData] = useState([]);
  const { id } = useParams();

  const convertTestAnalysis = (testAnalysis) => {
    if (!testAnalysis) return {};

    const finalPerformanceFormat = {
      student_id: testAnalysis.enquiryId,
      tests: [],
    };

    testAnalysis.tests?.forEach((test) => {
      const finalTestData = {
        test_name: test.name,
        test_id: test.id,
        subjects: {},
        total_marks: 0,
        maxMarks: 0,
      };

      test.keywordwiseScore?.forEach((keyword) => {
        finalTestData.subjects[keyword.keywordName] = Number(keyword.scored);
        finalTestData.total_marks += Number(keyword.scored || 0);
        finalTestData.maxMarks += Number(keyword.maxMarks || 0);
      });

      finalPerformanceFormat.tests.push(finalTestData);
    });

    return finalPerformanceFormat;
  };

  const fetchCoursesData = async () => {
    try {
      const res = await ApiService.fetchRecomendationEngine();
      console.log(res.data?.courses);
      return res.data?.courses;
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  useEffect(() => {
    const fetchAnalysisData = async () => {
      try {
        const [res, topicWise] = await Promise.all([
          ApiService.fetchTestAnalysis(id),
          fetchCoursesData(),
        ]);
        const finalData = convertTestAnalysis(res.data);
        console.log(finalData, "here");
        setCoursesData(topicWise);
        setTestPerformances(finalData);
      } catch (error) {
        console.log(error);
      }
    };

    fetchAnalysisData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log(testPerformances.tests, "tests");
    if (testPerformances?.tests) {
      const newSubjectLabels = new Set();

      testPerformances.tests.forEach((test) => {
        for (let subject in test.subjects) {
          if (newSubjectLabels.has(test)) continue;

          newSubjectLabels.add(subject);
        }
      });
      setSubjectLabels([...newSubjectLabels]);

      const newTestData = testPerformances.tests.map((test) =>
        Object.values(test.subjects)
      );
      setTestData(newTestData);
      const newTestLabels = testPerformances.tests.map(
        (test) => test.test_name
      );
      setTestLabels(newTestLabels);
      const newTotalMarksData = testPerformances.tests.map(
        (test) => test.total_marks
      );
      setTotalMarksData(newTotalMarksData);
    }
  }, [testPerformances]);

  const classesBarGraph = useStylesBarGraph();
  const classesRadarGraph = useStylesRadarGraph();

  const changeDisplaying = (method) => {
    setDisplaying(method);
  };

  const renderingMap = {
    tabular: (
      <TabularData
        testPerformances={testPerformances}
        subjectLabels={subjectLabels}
      />
    ),
    performance: <PerformanceProfiling coursesData={coursesData} />,
  };

  return (
    <div>
      <div className="button-wrappers">
        <div
          onClick={() => changeDisplaying("tabular")}
          className={`button-text button-border ${displaying === "tabular" ? "analysis-button-active" : ""
            }`}
        >
          Test Results Summary
          <i className="test-result-info">
            i{" "}
            <span className="test-result-legend test-result-right-legend analysis-legend">
              A concise summary of your test results, showcasing your performance in an easily digestible format.
            </span>
          </i>
        </div>
        <div
          className={`button-text button-border ${displaying === "graph" ? "analysis-button-active" : ""
            }`}
          onClick={() => changeDisplaying("graph")}
        >
          Performance Trends Explorer
          <i className="test-result-info">
            i{" "}
            <span className="test-result-legend test-result-right-legend analysis-legend">
              Explore the trends in your performance across various tests
              through interactive graphs and charts.
            </span>
          </i>
        </div>
        <div
          className={`button-text ${displaying === "performance" ? "analysis-button-active" : ""
            }`}
          onClick={() => changeDisplaying("performance")}
        >
          Topics Mastery
          <i className="test-result-info">
            i{" "}
            <span className="test-result-legend test-result-left-legend analysis-legend">
              Profiler & analyze your strengths across different topics to guide your study and improvement strategies.
            </span>
          </i>
        </div>
      </div>
      {testPerformances?.tests?.length ? (
        displaying === "graph" ? (
          <div
            style={{
              marginLeft: "10px",
              marginRight: "10px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Card className={classesBarGraph.card}>
              <CardContent>
                <StackedBarLineGraph
                  testLabels={testLabels}
                  subjectLabels={subjectLabels}
                  testData={testData}
                  totalMarksData={totalMarksData}
                />
              </CardContent>
            </Card>
            <div
              style={{
                marginTop: "3px",
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Card className={classesRadarGraph.card}>
                <CardContent>
                  <h6 style={{ margin: "10px" }}>
                    Sum of Marks by Section and Testname
                  </h6>
                  <SpiderGraph
                    subjectLabels={subjectLabels}
                    testLabels={testLabels}
                    testData={testData}
                  />
                </CardContent>
              </Card>
              <Card className={classesRadarGraph.card}>
                <CardContent>
                  <h6 style={{ margin: "10px" }}>
                    Sum of Marks by Testname and Section
                  </h6>
                  <RadarGraph
                    testLabels={testLabels}
                    subjectLabels={subjectLabels}
                    testData={testData}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          renderingMap[displaying]
        )
      ) : testPerformances?.tests?.length === 0 ? (
        <div
          style={{
            fontSize: "25px",
            textAlign: "center",
            padding: "20px",
            color: "var(--danger)",
            fontWeight: 700,
          }}
        >
          Please take at least one test to view your analysis results here.
        </div>
      ) : null}
    </div>
  );
};

export default TestAnalysis;
